import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { LocalService } from '../../../services/storage/local.service';
import { Observable, of } from 'rxjs';
import { UserApiKey } from '../models/user';
import { User } from '../models/user.type';
import { Constants } from '../../../utils/Constants';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly user = signal(null);
  private readonly USER_KEY = 'currentUser';

  constructor(
    private readonly httpClient: HttpClient,
    private readonly localStore: LocalService,
  ) {
    const storedUser = this.localStore.getObjData(this.USER_KEY);
    this.user.set(storedUser);
  }

  updateUser(user) {
    return this.httpClient.post(`${environment.api_url}user/edit`, user);
  }

  updateShopUser(user) {
    return this.httpClient.post(`${environment.api_url}user/update`, user);
  }

  setUser(user: object): void {
    this.localStore.saveObjData(this.USER_KEY, user);
    this.user.set(user);
  }

  hasRole(roles: string[]) {
    const userRoles = this.getUser()?.relatedRoles.map((r) => r.roleCode);
    return userRoles?.some((r) => roles.includes(r) || r === Constants.ROLE_SUPER_ADMIN);
  }

  hasRoleSeller() {
    const roles = Constants.ALL_SELLER_ROLES;
    const userRoles = this.getUser()?.relatedRoles.map((r) => r.roleCode);
    return userRoles?.some((r) => roles.includes(r));
  }

  isSeller(userReq = null): boolean {
    const user = userReq ?? this.getUser();
    const roles = Constants.ALL_SELLER_ROLES;
    const userRoles = user?.relatedRoles.map((r) => r.roleCode);
    return userRoles?.some((r) => roles.includes(r));
  }

  get isSuperAdmin() {
    const user = this.getUser();
    const roles = Constants.ROLE_SUPER_ADMIN;
    const userRoles = user?.relatedRoles.map((r) => r.roleCode);
    return userRoles?.some((r) => roles.includes(r));
  }

  get isOperator() {
    const user = this.getUser();
    const roles = Constants.ALL_OPERATOR_ROLES;
    const userRoles = user?.relatedRoles.map((r) => r.roleCode);
    return userRoles?.some((r) => roles.includes(r));
  }

  isDefaultSeller(user: User) {
    return user?.defaultRole?.roleCode && Constants.ALL_SELLER_ROLES.includes(user?.defaultRole?.roleCode);
  }

  getUser(): any {
    return this.user();
  }

  getLanguages(): Observable<any> {
    return this.httpClient.get(`${environment.api_url}language/all`);
  }

  processUser() {
    return this.httpClient.get(`${environment.api_url}user/infos`);
  }

  disableEnableUser(data) {
    return this.httpClient.put(`${environment.api_url}user/disableEnableAccount`, data);
  }


  resetPassword(email) {
    return this.httpClient.put(`${environment.api_url}user/seller/send-reset-password-email`, email);
  }

  logout() {
    this.localStore.clearData();
  }
  generateApiKey(): Observable<UserApiKey> {
    return this.httpClient.get<UserApiKey>(`${environment.api_url}user/generateApiKey`);
  }

  getUsersOfShop(data): Observable<any> {
    return this.httpClient.post(`${environment.api_url}user/getSellerUsersByShopCode`, data);
  }

  getUsers(data): Observable<any> {
    return this.httpClient.post(`${environment.api_url}user/operatorUsers`, data);
  }

  getUsersForSuperAdmin(data): Observable<any> {
    return this.httpClient.post(`${environment.api_url}user/superAdmin/all`, data);
  }

  getSellerRoles() {
    return this.httpClient.get(`${environment.api_url}role/seller/findAll`);
  }

  getOperatorRoles() {
    return this.httpClient.get(`${environment.api_url}role/operator/findAll`);
  }

  getAdminRoles() {
    return this.httpClient.get(`${environment.api_url}role/superAdmin/findAll`);
  }

  asscoiateRolesToSellers(data) {
    return this.httpClient.post(`${environment.api_url}user/assignRemoveRoles`, data);
  }

  asscoiateShopsToOperators(data) {
    return this.httpClient.post(`${environment.api_url}user/assignOperatorShop`, data);
  }

  getShops(shopCode) {
    if (this.hasRoleSeller()) {
      return of(this.user().relatedShops);
    } else {
      return this.httpClient.get(`${environment.api_url}shop/shopsInLegalEntity?shopCode=${shopCode}`);
    }
  }

  createUser(user: User) {
    const userCreation = this.userToUserCreation(user);
    return this.httpClient.post(`${environment.api_url}user/create`, userCreation);
  }

  userToUserCreation(user: User) {
    return {
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      shopCode: user.defaultShop.shopCode,
      phoneNumber: user.phoneNumber,
      languageCode: user.language.languageCode,
      roleCode: user.defaultRole.roleCode
    };
  }
  exportUsersForAdminToExcel(data) {
    return this.httpClient.post(`${environment.api_url}export/admin/users`, data, { responseType: 'blob', observe: 'response' })
  } 
  
  exportUsersForOperatorToExcel(data) {
    return this.httpClient.post(`${environment.api_url}export/operator/users`, data, { responseType: 'blob', observe: 'response' })
  }
  
  exportUsersForSellerToExcel(data) {
    return this.httpClient.post(`${environment.api_url}export/seller/users`, data, { responseType: 'blob', observe: 'response' })
  }

}
